import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const pageContentVariants = cva('w-full flex items-start justify-between gap-y-[60px] gap-x-[120px] 2xl:gap-x-[240px]', {
  variants: {
    variant: {
      default: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse',
      fill: 'flex-wrap items-stretch flex-grow'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const Page = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    id="page"
    className={cn(pageContentVariants({ variant }), className)}
    {...props}
  />
));
Page.displayName = 'Page';

const pageColVariants = cva('flex-grow basis-0 min-w-[280px] md:min-w-[300px] max-w-full', {
  variants: {
    variant: {
      default: 'flex flex-col gap-5'
    },
    bg: {
      true: 'bg-white shadow-dropshadow-sm py-5 px-8 rounded-xl'
    }
  },
  defaultVariants: {
    variant: 'default',
    bg: false
  }
});

const PageContent = React.forwardRef(({ className, variant, bg, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-page-content', pageColVariants({ variant, bg }), className)}
    {...props}
  />
));
PageContent.displayName = 'PageContent';

const PageContentHeader = React.forwardRef(({ className, variant, bg, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-page-content-header',
      // styles
      'mb-8 flex items-center gap-3 border-b border-solid border-gray-200 pb-3',
      className
    )}
    {...props}
  />
));
PageContentHeader.displayName = 'PageContentHeader';

const PageContentTitle = React.forwardRef(({ className, variant, bg, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'c-page-content-title',
      // styles
      'mb-0 text-base font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
PageContentTitle.displayName = 'PageContentTitle';

const pageSidebarVariants = cva('w-full max-w-full flex flex-col', {
  variants: {
    variant: {
      default: 'lg:w-[240px] 2xl:w-[344px]',
      md: 'lg:w-[320px] 2xl:w-[344px]',
      chat: 'lg:w-[344px]',
      sticky: 'sticky top-0',
      calendar: 'lg:w-64'
    },
    overflow: {
      true: 'overflow-y-auto overflow-x-hidden',
      false: ''
    },
    bg: {
      true: 'bg-white shadow-dropshadow-sm rounded-[10px] p-5',
      false: ''
    },
    forum: {
      true: 'lg:max-h-[calc(100vh-120px)] px-2'
    }
  },
  defaultVariants: {
    variant: 'default',
    overflow: true,
    bg: false,
    forum: false
  }
});

const PageSidebar = React.forwardRef(({ className, variant, overflow, bg, forum, ...props }, ref) => (
  <aside
    ref={ref}
    id="page-sidebar"
    className={cn(pageSidebarVariants({ variant, overflow, bg, forum }), className)}
    {...props}
  />
));
PageSidebar.displayName = 'PageSidebar';

const PageSidebarTitle = React.forwardRef(({ className, variant = 'default', ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'page-sidebar-title',
      // Estilo base
      variant === 'default' && 'mb-2 text-xl font-bold text-primary-dark',
      variant === 'outline' && 'mb-6 inline-block self-start rounded border border-solid border-neutral-lightest px-3 py-1 text-sm font-light text-primary-dark',
      className
    )}
    {...props}
  />
));
PageSidebarTitle.displayName = 'PageSidebarTitle';

const pageSidebarTextVariants = cva('text-[14px] font-light text-neutral-dark mb-4', {
  variants: {
    variant: {
      tip: 'c-page-sidebar-text-tip bg-secondary-lightest px-4 py-[10px]',
      excerpt: 'c-page-sidebar-text-excerpt line-clamp-2',
      highlight: 'c-page-sidebar-text-highlight rounded-[10px] bg-bright p-[10px]'
    }
  },
  defaultVariants: {
    variant: null
  }
});

const PageSidebarText = React.forwardRef(({ className, variant, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-page-sidebar-text', pageSidebarTextVariants({ variant }), className)}
    {...props}
  />
));
PageSidebarText.displayName = 'PageSidebarText';

const PageSidebarTextGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-page-sidebar-text-group', 'flex flex-wrap gap-3 [&>*]:mb-0 [&>*]:flex-grow', className)}
    {...props}
  />
));
PageSidebarTextGroup.displayName = 'PageSidebarTextGroup';

const PageSidebarIcon = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-page-sidebar-icon', 'mb-5 [&_img]:size-10', className)}
    {...props}
  />
));
PageSidebarIcon.displayName = 'PageSidebarIcon';

const PageSidebarList = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('c-page-sidebar-list', 'ml-5 list-disc', '[&_li]:text-[14px] [&_li]:font-light [&_li]:text-neutral-dark', className)}
    {...props}
  />
));
PageSidebarList.displayName = 'PageSidebarList';

export { Page, PageContent, PageContentHeader, PageContentTitle, PageSidebar, PageSidebarTitle, PageSidebarText, PageSidebarTextGroup, PageSidebarIcon, PageSidebarList };
